import React, { useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { reduxForm, change } from 'redux-form';
import { Alert, Button, Row, Col, Form, FormGroup } from 'reactstrap';
import { Utils } from '../../services';
import FormLayout from '../../models/FormLayout';
import {
    formGroupReduxPrefix,
    formRendererInitialValuesWithGroups,
    formValuesByFieldNameWithGroups,
    getRootLayoutSequence,
    getSortedFieldsInGroup,
    getSortedSteps,
    getStepLayoutSequence,
} from '../../services/formLayoutGroupUtils';
import {
    getInvalidStepFieldPaths,
    isCurrentStepValid,
    resolveStepTransitionClass,
} from '../../services/formWizardStepUtils';
import FormRendererBreadcrumb from './FormRendererBreadcrumb';
import { isSubmitValidationOk } from '../../services/workflowSubmitValidation';
import { FormRendererRepeatableGroup, FormRendererSingleGroup } from './FormRendererGroupSection';

import './FormRenderer.css';

function FieldRequiredError({ show, message }) {
    if (!show) {
        return null;
    }
    return (
        <div className="invalid-feedback d-block form-wizard-field-error-msg">
            {message}
        </div>
    );
}

function formRendererInitialValuesFromLayout(formLayoutRaw, resources) {
    const fl = formLayoutRaw instanceof FormLayout ? formLayoutRaw : new FormLayout(formLayoutRaw || { fields: [] });
    if (!fl.fields?.length) {
        return {};
    }
    const initial = {};
    fl.fields.forEach((fld) => {
        if (fld.groupId ?? fld.group_id) {
            return;
        }
        if (typeof fld.getFormInitialValue !== 'function') {
            return;
        }
        const v = fld.getFormInitialValue(resources);
        if (v === undefined || v === '') {
            return;
        }
        if (Array.isArray(v) && v.length === 0) {
            return;
        }
        initial[fld.id] = v;
    });
    return initial;
}

function resolveFormLayoutModel(formLayoutRaw) {
    if (!formLayoutRaw) {
        return new FormLayout({ fields: [], groups: [] });
    }
    return formLayoutRaw instanceof FormLayout ? formLayoutRaw : new FormLayout(formLayoutRaw);
}

function formValuesByFieldName(formLayout, formValues) {
    const out = {};
    const fields = formLayout?.fields || [];
    fields.forEach((field) => {
        const name = String(field?.name || '').trim();
        const id = field?.id;
        if (!name || id == null)
            return;
        if (Object.prototype.hasOwnProperty.call(formValues || {}, id)) {
            out[name] = formValues[id];
        }
    });
    return out;
}

let FormRenderer = props => {
    const dispatch = useDispatch();

    const { handleSubmit, resources } = props;
    const { formLayout, isLoading, className, submitLabel, submitIcon, submitValidation, submitDisabled = false, showFormName = true } = props;
    const { defaultValues, preFields, postFields }          = props;

    const { t, i18n } = useTranslation();
    const { user }    = useSelector(state => state.auth);
    const { setting: reduxSetting } = useSelector(state => state.settings);
    const formValues = useSelector(state => state.form?.formRenderer?.values || {});

    const uiLang = Utils.i18nUiLang(i18n);

    const layoutModel = React.useMemo(() => resolveFormLayoutModel(formLayout), [formLayout]);
    const hasGroups = (layoutModel.groups || []).length > 0;
    const setting = props.setting || reduxSetting;

    const context = React.useMemo(() => ({
        lang: uiLang,
        resources,
        setting,
    }), [uiLang, resources, setting]);

    const submitValidationSource = React.useMemo(() => {
        const namedFlat = hasGroups
            ? formValuesByFieldNameWithGroups(layoutModel, formValues)
            : formValuesByFieldName(layoutModel, formValues);
        return {
            ...namedFlat,
            ...(defaultValues || {}),
            ...(formValues || {}),
            form: {
                ...namedFlat,
                ...(formValues || {}),
            },
        };
    }, [defaultValues, formValues, hasGroups, layoutModel]);
    const isSubmitEnabled = React.useMemo(
        () => isSubmitValidationOk(submitValidation, submitValidationSource),
        [submitValidation, submitValidationSource]
    );

    useEffect(() => {
        if (user) {
            const updatable = [
                'firstname', 'lastname', 'email', 'address',
                'avs', 'bank', 'bic', 'birthday', 'city',
                'country', 'email', 'fax', 'firstname',
                'fix_phone', 'gender', 'iban', 'lastname',
                'mobile_phone', 'pro_phone', 'zip'
            ];
            layoutModel.fields
                .filter(f => !(f.groupId ?? f.group_id) && updatable.includes(f.name))
                .forEach(field => {
                    dispatch(change('formRenderer', field.id, user[field.name]));
                });
            (layoutModel.groups || []).forEach((group) => {
                if (group.multiple) {
                    return;
                }
                const prefix = formGroupReduxPrefix(group.id);
                getSortedFieldsInGroup(layoutModel.fields, group.id).forEach((field) => {
                    if (FormLayout.isDisplayOnlyField(field.type)) {
                        return;
                    }
                    if (updatable.includes(field.name) && user[field.name] !== undefined) {
                        dispatch(change('formRenderer', `${prefix}.${field.id}`, user[field.name]));
                    }
                });
            });
        }
    }, [dispatch, layoutModel.fields, layoutModel.groups, user]);

    useEffect(() => {
        if (defaultValues) {
            const updatable = Object.keys(defaultValues);
            layoutModel.fields
                .filter(f => !(f.groupId ?? f.group_id) && updatable.includes(f.name))
                .forEach(field => {
                    dispatch(change('formRenderer', field.id, defaultValues[field.name]));
                });
            (layoutModel.groups || []).forEach((group) => {
                if (group.multiple) {
                    return;
                }
                const prefix = formGroupReduxPrefix(group.id);
                getSortedFieldsInGroup(layoutModel.fields, group.id).forEach((field) => {
                    if (FormLayout.isDisplayOnlyField(field.type)) {
                        return;
                    }
                    if (updatable.includes(field.name) && defaultValues[field.name] !== undefined) {
                        dispatch(change('formRenderer', `${prefix}.${field.id}`, defaultValues[field.name]));
                    }
                });
            });
        }
    }, [defaultValues, dispatch, layoutModel.fields, layoutModel.groups]);

    const wizardSteps = React.useMemo(
        () => getSortedSteps(layoutModel.steps || []),
        [layoutModel.steps]
    );
    const useWizard = wizardSteps.length > 0;
    const [wizardStepIndex, setWizardStepIndex] = React.useState(0);
    const [wizardAnimToken, setWizardAnimToken] = React.useState(0);
    const [stepShowErrors, setStepShowErrors] = React.useState(false);
    const formRootRef = React.useRef(null);

    useEffect(() => {
        setWizardStepIndex(0);
        setWizardAnimToken(0);
        setStepShowErrors(false);
    }, [layoutModel.id]);

    useEffect(() => {
        setStepShowErrors(false);
    }, [wizardStepIndex]);

    const activeWizardStep = wizardSteps[wizardStepIndex];
    const isLastWizardStep = !useWizard || wizardStepIndex >= wizardSteps.length - 1;
    const invalidStepPaths = React.useMemo(() => {
        if (!useWizard || !stepShowErrors) {
            return new Set();
        }
        return new Set(getInvalidStepFieldPaths(layoutModel, wizardStepIndex, formValues));
    }, [useWizard, stepShowErrors, layoutModel, wizardStepIndex, formValues]);
    const stepHasVisibleErrors = invalidStepPaths.size > 0;
    const fieldRequiredMessage = t('forms.field_required');

    const scrollToFirstStepError = React.useCallback(() => {
        // Wait for the error markup to commit before scrolling.
        setTimeout(() => {
            const root = formRootRef.current;
            const el = root && root.querySelector('[data-wizard-error="true"]');
            if (el && typeof el.scrollIntoView === 'function') {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 0);
    }, []);

    const goToWizardStep = React.useCallback((index) => {
        setWizardStepIndex(index);
        setWizardAnimToken((x) => x + 1);
        setStepShowErrors(false);
    }, []);

    const handleWizardNext = React.useCallback(() => {
        if (!isCurrentStepValid(layoutModel, wizardStepIndex, formValues)) {
            setStepShowErrors(true);
            scrollToFirstStepError();
            return;
        }
        goToWizardStep(Math.min(wizardSteps.length - 1, wizardStepIndex + 1));
    }, [
        formValues,
        goToWizardStep,
        layoutModel,
        scrollToFirstStepError,
        wizardStepIndex,
        wizardSteps.length,
    ]);

    const onWizardFormSubmit = (useWizard && !isLastWizardStep)
        ? (e) => {
            e.preventDefault();
            handleWizardNext();
        }
        : handleSubmit;

    const renderSequence = (sequence) => sequence.map((item) => {
        if (item.kind === 'group') {
            const group = item.ref;
            if (group.multiple) {
                return (
                    <Col key={group.id} sm={12}>
                        <FormRendererRepeatableGroup
                            group={group}
                            allFields={layoutModel.fields}
                            context={context}
                            t={t}
                            invalidPaths={invalidStepPaths}
                            fieldRequiredMessage={fieldRequiredMessage}
                        />
                    </Col>
                );
            }
            return (
                <Col key={group.id} sm={12}>
                    <FormRendererSingleGroup
                        group={group}
                        allFields={layoutModel.fields}
                        context={context}
                        invalidPaths={invalidStepPaths}
                        fieldRequiredMessage={fieldRequiredMessage}
                    />
                </Col>
            );
        }
        const field = FormLayout.createField(item.ref) || item.ref;
        const fieldPath = String(field.id);
        const hasError = invalidStepPaths.has(fieldPath);
        return (
            <Col
                key={field.id}
                sm={12}
                md={Math.round(field.width * 12)}
                data-wizard-error={hasError ? 'true' : undefined}
            >
                <FormGroup row className={hasError ? 'form-wizard-field--invalid' : undefined}>
                    {field.getLabel(context) && (
                        <Col sm={12} className="text-left font-weight-bold">
                            {field.getLabel(context)}
                        </Col>
                    )}
                    {field.getDescription(context) && (
                        <Col sm={12} className="text-left">
                            {field.getDescription(context)}
                        </Col>
                    )}
                    <Col sm={12} className={hasError ? 'form-wizard-field-input--invalid' : undefined}>
                        {field.getInput(context)}
                        <FieldRequiredError show={hasError} message={fieldRequiredMessage} />
                    </Col>
                </FormGroup>
            </Col>
        );
    });

    const wizardRowSequence = React.useMemo(() => {
        if (!useWizard || !activeWizardStep) {
            return null;
        }
        return getStepLayoutSequence(
            layoutModel.fields,
            layoutModel.groups,
            wizardSteps,
            activeWizardStep.id
        );
    }, [useWizard, activeWizardStep, layoutModel.fields, layoutModel.groups, wizardSteps]);

    const breadcrumbStyle = layoutModel.breadcrumbStyle || layoutModel.breadcrumb_style || 'dots';
    const showStepTitle = layoutModel.showStepTitle !== false && layoutModel.show_step_title !== false;

    return (
        <div className={`FormRenderer ${className || ''}`} ref={formRootRef}>
            {showFormName && (
                <h3 className="mb-3">
                    { Utils.localized_or_fallback(layoutModel.name, uiLang) }
                </h3>
            )}
            {useWizard && (
                <FormRendererBreadcrumb
                    steps={wizardSteps}
                    lang={uiLang}
                    style={breadcrumbStyle}
                    currentIndex={wizardStepIndex}
                    onJump={goToWizardStep}
                />
            )}
            <Form onSubmit={onWizardFormSubmit}>
                <Row>
                    { preFields }
                    {useWizard && activeWizardStep && showStepTitle && (
                        <Col sm={12}>
                            <h4 className="h5 mb-3 text-left">
                                {Utils.localized_or_fallback(
                                    activeWizardStep.label,
                                    uiLang,
                                    activeWizardStep.name || ''
                                )}
                            </h4>
                        </Col>
                    )}
                    {useWizard && stepHasVisibleErrors && (
                        <Col sm={12}>
                            <Alert color="danger" className="text-left">
                                {t('forms.step_required_fields')}
                            </Alert>
                        </Col>
                    )}
                    {useWizard && wizardRowSequence && (
                        <Col
                            sm={12}
                            key={`wizard-panel-${wizardStepIndex}-${wizardAnimToken}`}
                            className={`form-wizard-step-panel ${resolveStepTransitionClass(activeWizardStep, layoutModel.stepTransition)}`}
                        >
                            <Row>
                                {renderSequence(wizardRowSequence)}
                            </Row>
                        </Col>
                    )}
                    {!useWizard && hasGroups
                        ? renderSequence(getRootLayoutSequence(layoutModel.fields, layoutModel.groups))
                        : null}
                    {!useWizard && !hasGroups
                        ? layoutModel.fields.sort((a, b) => a.position - b.position).map((field) => (
                            <Col key={field.id} sm={12} md={Math.round(field.width * 12)}>
                                <FormGroup row>
                                    {field.getLabel(context) && (
                                        <Col sm={12} className="text-left font-weight-bold">
                                            {field.getLabel(context)}
                                        </Col>
                                    )}
                                    {field.getDescription(context) && (
                                        <Col sm={12} className="text-left">
                                            {field.getDescription(context)}
                                        </Col>
                                    )}
                                    <Col sm={12}>
                                        {field.getInput(context)}
                                    </Col>
                                </FormGroup>
                            </Col>
                        ))
                        : null}
                    {(!useWizard || isLastWizardStep) && postFields}
                </Row>
                <FormGroup row className="mt-3 d-flex flex-wrap align-items-center">
                    <Col xs="auto" className="mb-2">
                        {useWizard && wizardStepIndex > 0 && (
                            <Button
                                type="button"
                                color="secondary"
                                className="mr-2"
                                disabled={isLoading}
                                onClick={() => goToWizardStep(Math.max(0, wizardStepIndex - 1))}
                            >
                                <i className="fa fa-arrow-left" />
                                &nbsp;
                                {t('forms.previous')}
                            </Button>
                        )}
                    </Col>
                    <Col xs="auto" className="mb-2">
                        {useWizard && !isLastWizardStep && (
                            <Button
                                type="button"
                                color="info"
                                disabled={isLoading}
                                onClick={handleWizardNext}
                            >
                                <i className={`fa fa-${activeWizardStep?.nextIcon || activeWizardStep?.next_icon || 'arrow-right'}`} />
                                &nbsp;
                                {Utils.localized_or_fallback(
                                    activeWizardStep?.nextLabel || activeWizardStep?.next_label || {},
                                    uiLang,
                                    t('forms.next')
                                )}
                            </Button>
                        )}
                    </Col>
                    <Col xs="auto" className="mb-2">
                        {(!useWizard || isLastWizardStep) && (
                            <Button type="submit" color="info" disabled={isLoading || !isSubmitEnabled || submitDisabled}>
                                <i className={`fa ${isLoading ? 'fa-spinner fa-spin' : `fa-${submitIcon || 'save'}`}`}></i>&nbsp;
                                { submitLabel || t('common.save') }
                            </Button>
                        )}
                    </Col>
                </FormGroup>
            </Form>
        </div>
    );
}

FormRenderer = reduxForm({
    form: 'formRenderer',
    enableReinitialize: true
})(FormRenderer);

FormRenderer = connect(
    (_state, own_props) => {
        const fl = resolveFormLayoutModel(own_props.formLayout);
        const resources = own_props.resources;
        const initial = (fl.groups || []).length > 0
            ? formRendererInitialValuesWithGroups(fl, resources)
            : formRendererInitialValuesFromLayout(fl, resources);
        return { initialValues: initial };
    }, {}
)(FormRenderer)

export default FormRenderer;
